import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '@/hooks/useDashboardContext';
import { marketing } from '@/lib/supabase/marketing';
import {
  computeMetric,
  effectivePeriodRange,
  getCompareRange,
  toISTDateString,
  type CompareMode,
  type DateRange,
  type Metric,
  type PeriodKey,
} from '@/lib/periods';

export type MetricSource =
  | 'net_sales'
  | 'orders'
  | 'covers'
  | 'avg_ticket'
  | 'aggregator_gmv'
  | 'aggregator_orders'
  | 'aggregator_ads_spend';

interface UseMetricArgs {
  outletId: string | 'all';
  source: MetricSource;
  period: PeriodKey;
  compareMode?: CompareMode;
}

interface UseMetricResult {
  metric: Metric | null;
  current: DateRange;
  previous: DateRange;
  trend: { date: string; current: number; previous?: number }[];
}

const TABLE: Record<MetricSource, { table: 'mv_outlet_kpi_daily'; column: string }> = {
  net_sales: { table: 'mv_outlet_kpi_daily', column: 'net_sales' },
  orders: { table: 'mv_outlet_kpi_daily', column: 'orders' },
  covers: { table: 'mv_outlet_kpi_daily', column: 'covers' },
  avg_ticket: { table: 'mv_outlet_kpi_daily', column: 'avg_ticket' },
  aggregator_gmv: { table: 'mv_outlet_kpi_daily', column: 'aggregator_gmv' },
  aggregator_orders: { table: 'mv_outlet_kpi_daily', column: 'aggregator_orders' },
  aggregator_ads_spend: { table: 'mv_outlet_kpi_daily', column: 'aggregator_ads_spend' },
};

async function sumRange(
  outletId: string | 'all',
  column: string,
  range: DateRange,
): Promise<{ total: number; series: { date: string; value: number }[] }> {
  const start = toISTDateString(range.start);
  const end = toISTDateString(new Date(range.end.getTime() - 1));

  let q = marketing
    .from('mv_outlet_kpi_daily')
    .select(`date, ${column}`)
    .gte('date', start)
    .lte('date', end);
  if (outletId !== 'all') q = q.eq('outlet_id', outletId);

  const { data, error } = await q;
  if (error) throw error;

  const rows = (data ?? []) as unknown as Array<Record<string, unknown>>;
  const byDate = new Map<string, number>();
  for (const row of rows) {
    const date = String(row.date);
    const v = Number(row[column] ?? 0);
    byDate.set(date, (byDate.get(date) ?? 0) + v);
  }
  const series = Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, value]) => ({ date, value }));
  const total = series.reduce((acc, p) => acc + p.value, 0);
  return { total, series };
}

export function useMetric({
  outletId,
  source,
  period,
  compareMode = 'previous',
}: UseMetricArgs) {
  const { customRange } = useDashboard();
  const current = effectivePeriodRange(period, customRange);
  const previous = getCompareRange(current, compareMode);
  const { column } = TABLE[source];

  const query = useQuery<UseMetricResult>({
    queryKey: ['metric', source, outletId, current.start.toISOString(), previous.start.toISOString()],
    queryFn: async () => {
      const [cur, prev] = await Promise.all([
        sumRange(outletId, column, current),
        sumRange(outletId, column, previous),
      ]);
      const metric = computeMetric(cur.total, prev.total);
      const prevMap = new Map(prev.series.map((p) => [p.date, p.value]));
      const trend = cur.series.map((p, i) => ({
        date: p.date,
        current: p.value,
        previous: prev.series[i]?.value ?? prevMap.get(p.date),
      }));
      return { metric, current, previous, trend };
    },
    staleTime: 60_000,
  });

  return { ...query, current, previous };
}
